import { Add, Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, deDE } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { CreateLegalClientDialog } from '../../Clients/CreateLegalClientDialog';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { useGetLegalClients } from '../../hooks/reactQuerys/LegalClientQueries';
import { LoadingDialog } from '../../core/LoadingDialog';

interface IProps {
  limit?: number;
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: (newLegalClients: ILegalClient[]) => void;
  unmount: () => void;
}

const columns: GridColDef[] = [
  { field: 'idLegalClient', headerName: 'Nr.', flex: 1 },
  { field: 'ContactGroup', headerName: 'Gruppe', flex: 1 },
  {
    field: 'loginPossible',
    headerName: 'Login',
    flex: 1,
    renderCell: ({ value }) => (value ? <Check /> : <Close />),
  },
  {
    field: 'isReceivingEmailEnabled',
    headerName: 'E-Mails',
    flex: 1,
    renderCell: ({ value }) => (value ? <Check /> : <Close />),
  },
  { field: 'FirstName', headerName: 'Vorname', flex: 1 },
  { field: 'LastName', headerName: 'Nachname', flex: 1 },
  { field: 'Email', headerName: 'E-Mail', flex: 1 },
  { field: 'idArnoTopIntern', headerName: 'Namen.Nr.', flex: 1 },
];

export const AssignLegalClientsDialog: React.FC<IProps> = ({
  limit,
  takenLegalClients,
  setTakenLegalClients,
  unmount,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rowSelectionModel, setRowSelectionModel] = useState<number[]>(
    takenLegalClients.map(x => x.idLegalClient)
  );

  const { legalClients, isRefetchingLegalClients, refetchLegalClients } =
    useGetLegalClients();

  const filteredLegalClients = useMemo(() => {
    if (!legalClients) return [];
    const term = search.toLowerCase();
    return legalClients.filter(
      ({ FirstName, LastName, Email }) =>
        !term ||
        FirstName?.toLowerCase().includes(term) ||
        LastName?.toLowerCase().includes(term) ||
        Email?.toLowerCase().includes(term)
    );
  }, [search, legalClients]);

  const selectionCount = rowSelectionModel.length;

  const isReachedLimit = useMemo(
    () => !!(limit && rowSelectionModel.length === limit),
    [limit, rowSelectionModel.length]
  );

  const onAssign = () => {
    if (!legalClients) return;

    const takenIds = takenLegalClients.map(tlc => tlc.idLegalClient);

    const newTakenLegalClients = legalClients
      .filter(lc => rowSelectionModel.includes(lc.idLegalClient))
      .map(lc => {
        if (takenIds.includes(lc.idLegalClient)) {
          return takenLegalClients.find(
            tlc => tlc.idLegalClient === lc.idLegalClient
          )!;
        } else {
          return lc;
        }
      });

    setTakenLegalClients([...newTakenLegalClients]);

    unmount();
  };

  const onCreate = async (idLegalClient: number) => {
    setIsOpen(false);
    await refetchLegalClients();
    setRowSelectionModel(prev => [idLegalClient, ...prev]);
    setSearch('');
  };

  return (
    <>
      <Dialog open onClose={unmount} maxWidth='lg' fullWidth>
        {isRefetchingLegalClients || legalClients === undefined ? (
          <LoadingSpinner />
        ) : (
          <>
            <DialogTitle>
              <Typography variant='h4'>Mandanten zuordnen</Typography>
              {limit && (
                <Typography variant='subtitle1' color='textSecondary'>
                  {`Sie können maximal ${limit} Mandant/en auswählen.`}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  size='small'
                  label='Suche'
                  value={search}
                  fullWidth
                  onChange={e => setSearch(e.target.value)}
                />
                <IconButton
                  disabled={isReachedLimit}
                  onClick={() => setIsOpen(true)}
                >
                  <Add />
                </IconButton>
              </Box>
              <DataGrid
                sx={{ mt: 2 }}
                autoHeight
                checkboxSelection
                localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                rows={filteredLegalClients}
                onRowSelectionModelChange={newModel =>
                  setRowSelectionModel(
                    Array.from(
                      new Set(
                        (search === ''
                          ? newModel
                          : [
                              ...rowSelectionModel.filter(
                                id =>
                                  !filteredLegalClients.some(
                                    lc => lc.idLegalClient === id
                                  )
                              ),
                              ...newModel,
                            ]
                        ).slice(limit ? -limit : undefined)
                      )
                    ).map(Number) // Convert GridRowId[] to number[]
                  )
                }
                rowSelectionModel={rowSelectionModel}
                columns={columns}
                getRowId={({ idLegalClient }) => idLegalClient}
                isRowSelectable={({ row: { idLegalClient } }) =>
                  !isReachedLimit || rowSelectionModel.includes(idLegalClient)
                }
                initialState={{
                  pagination: { paginationModel: { pageSize: 15 } },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={unmount} variant='contained'>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                onClick={onAssign}
                disabled={!selectionCount || isRefetchingLegalClients}
              >
                Übernehmen
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {isOpen && (
        <CreateLegalClientDialog
          onSave={onCreate}
          unmount={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
